import { CartLinesUpdateEvent } from '@shopify/events';
import { Component } from '@theme/component';
import { buildRoutine, parseCatalogNode, normalizeProfile, sanitizeProfile } from '@theme/kunnie-routine-engine';
import { formatMoney } from '@theme/money-formatting';

const STORAGE_KEY = 'kunnie:routine:v2';
const QUESTIONS = ['hauttyp', 'empfindlich', 'bedarf', 'meiden', 'steps', 'budget'];
/** Questions answered with a single tap; all others are multi-choice with a "Weiter" button. */
const SINGLE = new Set(['hauttyp', 'empfindlich', 'steps', 'budget']);
const MAX_NEEDS = 3;
/** How long a tapped answer stays visible before the next question appears. */
const ADVANCE_DELAY = 300;
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

/** Translation keys use underscores (`anti-aging` → `opt_anti_aging`). */
const keyOf = (value) => String(value).replace(/-/g, '_');

const numericId = (gid) => String(gid ?? '').split('/').pop();

const session = {
  read() {
    try {
      const state = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      return state?.v === 2 ? state : null;
    } catch {
      return null;
    }
  },
  write(state) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Storage unavailable: the builder still works for this page view.
    }
  },
  clear() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  },
};

const freshState = () => ({
  v: 2,
  view: 'entry',
  consent: false,
  question: 0,
  /** @type {Record<string, any>} hauttyp: undefined = not answered, null = "weiß nicht" */
  profile: {},
  /** @type {any} Routine computed from the current localized catalogue. */
  routine: null,
  /** @type {Record<string, string>} step → chosen alternative handle */
  picks: {},
  time: 'morgens',
  savedId: null,
  /** Prevent duplicate submissions across reloads, including an uncertain response. */
  cartState: 'ready',
  cartId: '',
});

/**
 * Makes a stored state safe for the current builder. Older versions had a chat view (messages,
 * quick replies, free-text `notizen`); such a state starts again at the entry, keeping the consent.
 * @param {Record<string, any>} state
 */
function migrate(state) {
  const { messages, quickReplies, ready, ...rest } = state;
  // A chat profile may be half-filled from free text: start over (only the consent is kept).
  if (!['entry', 'cards', 'building', 'result'].includes(rest.view)) return { ...freshState(), consent: Boolean(rest.consent) };
  if (rest.profile && typeof rest.profile === 'object') {
    const { notizen, ...profile } = rest.profile;
    rest.profile = sanitizeProfile(profile);
  } else rest.profile = {};
  if (!rest.consent) rest.view = 'entry';
  if (rest.view === 'result' && !rest.routine) rest.view = 'entry';
  rest.question = Math.min(Math.max(Number(rest.question) || 0, 0), QUESTIONS.length - 1);
  return rest;
}

class KunnieRoutineBuilder extends Component {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {Record<string, string>} */
  #i18n = {};
  #state = freshState();
  #busy = false;
  #locale = document.documentElement.lang || 'de';
  /** @type {Set<string>} steps with an open alternatives panel */
  #openAlternatives = new Set();
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #advanceTimer;
  #run = 0;
  #cartBusy = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    try {
      // Liquid's `t` already HTML-escapes; decode once so #t escapes exactly once.
      const decoder = document.createElement('textarea');
      const raw = JSON.parse(this.querySelector('[data-i18n]')?.textContent || '{}');
      this.#i18n = Object.fromEntries(
        Object.entries(raw).map(([key, value]) => {
          decoder.innerHTML = String(value);
          return [key, decoder.value];
        })
      );
    } catch {
      this.#i18n = {};
    }

    this.#state = migrate({ ...freshState(), ...(session.read() || {}) });
    if (!['ready', 'added', 'uncertain'].includes(this.#state.cartState)) this.#state.cartState = 'ready';
    if (this.#state.view === 'result') this.#state.view = 'building';
    if (session.read()) this.#save();

    this.addEventListener('click', this.#onClick, { signal });
    this.querySelector('[data-consent]')?.addEventListener('change', this.#onConsent, { signal });
    this.querySelector('[data-save-form]')?.addEventListener('submit', this.#onSave, { signal });

    this.#render();
    if (this.#state.view === 'building') this.#build();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#run++;
    this.#controller?.abort();
    clearTimeout(this.#advanceTimer);
  }

  // ---------- helpers ----------

  /** @param {string} key @param {Record<string, string | number>} [values] */
  #t(key, values = {}) {
    let text = this.#i18n[key] ?? key;
    for (const [name, value] of Object.entries(values)) text = text.replaceAll(`%${name}%`, String(value));
    return escapeHtml(text);
  }

  /** Label of an option value (skin type, need, ingredient); unknown values as they are. @param {string} value */
  #opt(value) {
    const key = `opt_${keyOf(value)}`;
    return this.#i18n[key] ? this.#t(key) : escapeHtml(value);
  }

  /** Formats like Liquid's `money` filter (shop money format), so totals match the card prices. */
  #money(amount, currency = this.#state.routine?.currency || window.Shopify?.currency?.active || 'EUR') {
    const value = Number(amount) || 0;
    if (this.dataset.moneyFormat) return formatMoney(Math.round(value * 100), this.dataset.moneyFormat, currency);
    return new Intl.NumberFormat(this.#locale, { style: 'currency', currency }).format(value);
  }

  #save() {
    session.write(this.#state);
  }

  #announce(text) {
    const region = this.querySelector('[data-announce]');
    if (!region) return;
    region.textContent = '';
    requestAnimationFrame(() => (region.textContent = text));
  }

  /** Moves focus to the heading of the visible view/question and keeps it in view. */
  #focusView() {
    const view = this.querySelector(`[data-view="${this.#state.view}"]`);
    const question = view?.querySelector('[data-question]:not([hidden])');
    const target = /** @type {HTMLElement | null} */ ((question || view)?.querySelector('[data-focus]'));
    target?.focus({ preventScroll: true });
    const top = this.getBoundingClientRect().top;
    if (top < 0 || top > window.innerHeight * 0.5) {
      this.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    }
  }

  // ---------- API ----------

  /**
   * @param {object} body
   */
  async #api(body) {
    const response = await fetch(this.dataset.endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      signal: this.#controller.signal,
    });
    // Shopify replaces proxy failures with an HTML page, so only trust JSON.
    if (!response.ok || !(response.headers.get('content-type') || '').includes('application/json')) {
      throw new Error(`routine save failed (${response.status})`);
    }
    return response.json();
  }

  // ---------- render ----------

  #render() {
    for (const view of this.querySelectorAll('[data-view]')) {
      view.toggleAttribute('hidden', /** @type {HTMLElement} */ (view).dataset.view !== this.#state.view);
    }
    const consent = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-consent]'));
    if (consent) consent.checked = this.#state.consent;
    if (this.#state.view === 'cards') this.#renderQuestion();
    if (this.#state.view === 'result') this.#renderResult();
  }

  /** @param {string} view */
  #go(view, { focus = true } = {}) {
    clearTimeout(this.#advanceTimer);
    this.#state.view = view;
    this.#save();
    this.#render();
    if (focus) this.#focusView();
  }

  // ---------- entry ----------

  #onConsent = (event) => {
    this.#state.consent = /** @type {HTMLInputElement} */ (event.target).checked;
    if (this.#state.consent) this.querySelector('[data-consent-error]')?.setAttribute('hidden', '');
    this.querySelector('[data-consent-box]')?.classList.remove('is-invalid');
    this.#save();
  };

  #start() {
    if (!this.#state.consent) {
      const error = this.querySelector('[data-consent-error]');
      error?.removeAttribute('hidden');
      const box = this.querySelector('[data-consent-box]');
      box?.classList.remove('is-invalid');
      requestAnimationFrame(() => box?.classList.add('is-invalid'));
      /** @type {HTMLElement | null} */ (this.querySelector('[data-consent]'))?.focus();
      return;
    }
    this.#state.question = 0;
    this.#go('cards');
    this.#announceQuestion();
  }

  // ---------- card quiz ----------

  #answered(field) {
    const value = this.#state.profile[field];
    if (field === 'bedarf') return Array.isArray(value) && value.length > 0;
    if (field === 'meiden') return true;
    if (field === 'hauttyp') return value !== undefined;
    return value !== undefined && value !== null;
  }

  #renderQuestion() {
    const index = Math.min(Math.max(this.#state.question, 0), QUESTIONS.length - 1);
    const field = QUESTIONS[index];
    const { profile } = this.#state;

    for (const question of this.querySelectorAll('[data-question]')) {
      const active = /** @type {HTMLElement} */ (question).dataset.question === field;
      question.toggleAttribute('hidden', !active);
    }

    this.querySelector('[data-progress-label]').textContent = this.#i18n.q_progress
      ?.replace('%current%', String(index + 1))
      .replace('%total%', String(QUESTIONS.length)) ?? '';
    /** @type {HTMLElement} */ (this.querySelector('[data-progress-bar]')).style.inlineSize = `${((index + 1) / QUESTIONS.length) * 100}%`;

    for (const option of this.querySelectorAll(`[data-field="${field}"]`)) {
      const value = /** @type {HTMLElement} */ (option).dataset.value;
      let pressed = false;
      if (field === 'hauttyp') pressed = profile.hauttyp !== undefined && (profile.hauttyp ?? 'unsicher') === value;
      else if (field === 'empfindlich') pressed = profile.empfindlich !== undefined && (profile.empfindlich ? 'ja' : 'nein') === value;
      else if (field === 'steps') pressed = String(profile.steps) === value;
      else if (field === 'budget') pressed = profile.budget === value;
      else pressed = (profile[field] || []).includes(value);
      option.setAttribute('aria-pressed', String(pressed));
      if (field === 'bedarf') {
        const rank = (profile.bedarf || []).indexOf(value);
        const badge = option.querySelector('[data-rank]');
        if (badge) badge.textContent = rank >= 0 ? String(rank + 1) : '';
      }
    }

    if (field === 'bedarf') {
      const count = (profile.bedarf || []).length;
      this.querySelector('[data-need-count]').textContent = this.#i18n.need_count?.replace('%count%', String(count)) ?? '';
    }

    // "Weiter" exists only for multi-choice questions — single-choice ones advance on tap, so the
    // footer stays hidden there (also while the selection is shown before advancing).
    const foot = this.querySelector('[data-quiz-foot]');
    foot?.toggleAttribute('hidden', SINGLE.has(field));
    if (!SINGLE.has(field)) {
      const labels = /** @type {HTMLTemplateElement | null} */ (this.querySelector('[data-next-labels]'))?.content;
      const label = (name) => labels?.querySelector(`[data-label="${name}"]`)?.textContent?.trim() || name;
      const next = /** @type {HTMLButtonElement} */ (this.querySelector('[data-next]'));
      this.querySelector('[data-next-label]').textContent = field === 'meiden' && !(profile.meiden || []).length ? label('skip') : label('next');
      next.setAttribute('aria-disabled', String(!this.#answered(field)));
    }
    this.querySelector('[data-quiz-error]')?.setAttribute('hidden', '');
  }

  /** Screen readers: which question of how many (the focused heading reads the question itself). */
  #announceQuestion() {
    const index = Math.min(Math.max(this.#state.question, 0), QUESTIONS.length - 1);
    this.#announce(this.#i18n.q_progress?.replace('%current%', String(index + 1)).replace('%total%', String(QUESTIONS.length)) || '');
  }

  /** @param {HTMLElement} option */
  #choose(option) {
    const field = option.dataset.field;
    const value = option.dataset.value;
    const { profile } = this.#state;
    if (!field || value === undefined) return;

    if (field === 'bedarf' || field === 'meiden') {
      const list = [...(profile[field] || [])];
      const at = list.indexOf(value);
      if (at >= 0) list.splice(at, 1);
      else if (field === 'bedarf' && list.length >= MAX_NEEDS) {
        this.#announce(this.#i18n.need_max || '');
        option.classList.remove('is-shaking');
        requestAnimationFrame(() => option.classList.add('is-shaking'));
        return;
      } else list.push(value);
      profile[field] = list;
      this.#save();
      return this.#renderQuestion();
    }

    if (field === 'hauttyp') profile.hauttyp = value === 'unsicher' ? null : value;
    if (field === 'empfindlich') profile.empfindlich = value === 'ja';
    if (field === 'steps') profile.steps = Number(value);
    if (field === 'budget') profile.budget = value;
    this.#save();
    this.#renderQuestion();
    option.classList.remove('is-picked');
    requestAnimationFrame(() => option.classList.add('is-picked'));
    // Short pause so the selection is visible before moving on (every tap advances, also on the same answer).
    clearTimeout(this.#advanceTimer);
    const question = this.#state.question;
    this.#advanceTimer = setTimeout(() => {
      option.classList.remove('is-picked');
      if (this.#state.view === 'cards' && this.#state.question === question) this.#next();
    }, reducedMotion() ? 120 : ADVANCE_DELAY);
  }

  #next() {
    const field = QUESTIONS[this.#state.question];
    if (!this.#answered(field)) {
      const error = this.querySelector('[data-quiz-error]');
      if (error) {
        error.textContent = this.#i18n.need_required || '';
        error.removeAttribute('hidden');
      }
      return;
    }
    if (this.#state.question >= QUESTIONS.length - 1) return this.#build();
    this.#state.question += 1;
    this.#save();
    this.#renderQuestion();
    this.#focusView();
    this.#announceQuestion();
  }

  #back() {
    clearTimeout(this.#advanceTimer);
    if (this.#state.question <= 0) return this.#go('entry');
    this.#state.question -= 1;
    this.#save();
    this.#renderQuestion();
    this.#focusView();
    this.#announceQuestion();
  }

  // ---------- build ----------

  async #catalog() {
    const products = [];
    let pages = 1;
    for (let page = 1; page <= pages; page++) {
      const url = new URL(this.dataset.catalogUrl, location.origin);
      url.searchParams.set('section_id', 'kunnie-bundle-catalog');
      url.searchParams.set('page', String(page));
      const preview = new URLSearchParams(location.search).get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: this.#controller.signal });
      if (!response.ok) throw new Error('catalog');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const data = JSON.parse(doc.querySelector('[data-bundle-catalog]')?.textContent || 'null');
      if (data?.currency !== this.dataset.currency) throw new Error('currency');
      if (!Array.isArray(data.products) || !Number.isInteger(data.pages) || data.pages < 1 || data.pages > 100) throw new Error('catalog');
      pages = data.pages;
      for (const product of data.products) {
        const variants = product.variants.filter(v => /^\d+$/.test(v.id) && Number.isInteger(v.price) && v.price >= 0 && v.min === 1 && v.unit && new DOMParser().parseFromString(v.unit, 'text/html').body.textContent.trim());
        if (variants.length && Array.isArray(product.tags)) products.push({ ...product, variants });
      }
    }
    return [...new Map(products.map(p => [p.id, p])).values()];
  }

  #makeRoutine(products, restore = false) {
    const profile = normalizeProfile(this.#state.profile);
    const previous = restore ? Object.fromEntries((this.#state.routine?.steps || []).map(s => [s.step, this.#current(s)?.handle])) : {};
    const metadata = new Map();
    const catalog = products.map(p => {
      const v = p.variants[0];
      metadata.set(v.id, { product: p, variant: v });
      return parseCatalogNode({ id: `gid://shopify/Product/${p.id}`, handle: p.handle, title: p.title, vendor: p.vendor, tags: p.tags, status: 'ACTIVE', featuredMedia: { preview: { image: { url: v.image || p.image } } }, variants: { nodes: [{ id: `gid://shopify/ProductVariant/${v.id}`, price: (v.price / 100).toFixed(2), availableForSale: true }] } });
    }).filter(Boolean);
    const routine = buildRoutine(catalog, profile);
    routine.currency = this.dataset.currency;
    for (const step of routine.steps) {
      step.label = this.#i18n[`step_${keyOf(step.step)}`] || step.step;
      step.frequency = step.step === 'peeling' ? this.#i18n.frequency_peeling : step.step === 'maske' ? this.#i18n.frequency_mask : null;
      for (const item of [step.pick, ...step.alternatives]) {
        const { product, variant } = metadata.get(numericId(item.variantId));
        item.unit = variant.unit;
        item.url = product.url;
        const values = prefix => product.tags.filter(t => t.startsWith(prefix + ':')).map(t => t.slice(prefix.length + 1));
        const reasons = [];
        if (profile.hauttyp && values('hauttyp').includes(profile.hauttyp)) reasons.push(this.#i18n[`opt_${profile.hauttyp}`]);
        if (profile.empfindlich && values('hauttyp').includes('empfindlich')) reasons.push(this.#i18n.profile_sensitive);
        for (const need of profile.bedarf) if (values('bedarf').includes(need)) reasons.push(this.#i18n[`opt_${keyOf(need)}`]);
        for (const active of values('wirkstoff').slice(0, 2)) if (this.#i18n[`opt_${keyOf(active)}`]) reasons.push(this.#i18n[`opt_${keyOf(active)}`]);
        item.reason = [...new Set(reasons.filter(Boolean))].join(' · ');
      }
    }
    this.#state.routine = routine;
    this.#state.profile = profile;
    this.#state.picks = {};
    for (const step of routine.steps) if ([step.pick, ...step.alternatives].some(p => p.handle === previous[step.step])) this.#state.picks[step.step] = previous[step.step];
  }

  async #build() {
    if (this.#busy) return;
    const token = ++this.#run;
    const restore = Boolean(this.#state.routine);
    this.#busy = true;
    this.#go('building');
    this.querySelector('[data-building-error]')?.setAttribute('hidden', '');
    this.querySelector('[data-view="building"]')?.classList.remove('has-error');
    try {
      const products = await this.#catalog();
      if (token !== this.#run || !this.isConnected) return;
      this.#makeRoutine(products, restore);
      this.#state.savedId = null;
      this.#openAlternatives.clear();
      this.#busy = false;
      this.#go('result');
    } catch (error) {
      if (token !== this.#run || error.name === 'AbortError') return;
      this.#busy = false;
      this.querySelector('[data-view="building"]')?.classList.add('has-error');
      const box = this.querySelector('[data-building-error]');
      box?.removeAttribute('hidden');
      box?.querySelector('button')?.focus();
    }
  }

  #cardFallback(product) {
    return `<article class="kunnie-rb__card-fallback"><a href="${escapeHtml(product.url)}" aria-label="${escapeHtml(product.title)}">${product.image ? `<img src="${escapeHtml(product.image)}" alt="" loading="lazy" width="160" height="160">` : ''}</a><div>${product.vendor ? `<p class="kunnie-card__vendor">${escapeHtml(product.vendor)}</p>` : ''}<p class="kunnie-rb__card-title"><a href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a></p><p class="kunnie-rb__price">${this.#money(product.price)}</p>${product.unit || ''}</div></article>`;
  }

  #cartUi() {
    const blocked = ['added', 'uncertain'].includes(this.#state.cartState);
    const button = this.querySelector('[data-cart-add]');
    button.hidden = blocked;
    button.disabled = this.#cartBusy || !this.#products().length;
    button.setAttribute('aria-busy', String(this.#cartBusy));
    this.querySelector('[data-view-cart]').hidden = !blocked;
  }

  async #addAllToCart() {
    if (this.#cartBusy || this.#busy || this.#state.cartState !== 'ready' || !this.#products().length) return;
    this.#cartBusy = true;
    this.#cartUi();
    const errorBox = this.querySelector('[data-cart-error]');
    errorBox.hidden = true;
    let submitted = false;
    try {
      const before = this.#products().map(p => [numericId(p.variantId), Math.round(Number(p.price) * 100)]);
      const products = await this.#catalog();
      const variants = new Map(products.flatMap(p => p.variants.map(v => [v.id, v])));
      if (before.some(([id, price]) => !variants.has(id) || variants.get(id).price !== price)) {
        this.#makeRoutine(products, true);
        this.#state.savedId = null;
        this.#save();
        this.#renderResult();
        errorBox.textContent = this.#i18n.commerce_catalog_changed;
        errorBox.hidden = false;
        return;
      }
      const cartUrl = `${Theme.routes.cart_url}.js`;
      const readCart = async () => {
        const response = await fetch(cartUrl, { credentials: 'same-origin', cache: 'no-store', signal: this.#controller.signal });
        if (!response.ok) throw new Error('cart');
        return response.json();
      };
      const current = await readCart();
      if (current.currency !== this.dataset.currency) throw new Error('currency');
      const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map(e => e.dataset.sectionId).filter(Boolean))].slice(0, 5);
      this.#state.cartId ||= crypto.randomUUID();
      const items = before.map(([id]) => ({ id, quantity: 1, properties: { _kunnie_routine: this.#state.cartId } }));
      this.#state.cartState = 'uncertain';
      this.#save();
      submitted = true;
      const response = await fetch(Theme.routes.cart_add_url, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items, sections, sections_url: location.pathname }), signal: this.#controller.signal });
      const data = await response.json();
      const cart = await readCart();
      const confirmed = response.ok && !data.status && items.every(item => cart.items.some(line => String(line.variant_id) === item.id && line.quantity === 1 && line.properties?._kunnie_routine === this.#state.cartId));
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({ action: confirmed ? 'add' : 'update', context: 'product', lines: items.map(item => ({ merchandiseId: item.id, quantity: 1 })), promise: deferred.promise }));
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, sections: data.sections, source: 'kunnie-routine-builder', didError: !confirmed } });
      if (!confirmed) throw new Error('unconfirmed');
      this.#state.cartState = 'added';
      errorBox.textContent = this.#i18n.cart_added;
      errorBox.hidden = false;
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.#state.cartState = submitted ? 'uncertain' : 'ready';
      errorBox.textContent = error.message === 'currency' ? this.#i18n.commerce_currency_changed : submitted ? this.#i18n.cart_error : this.#i18n.commerce_load_error;
      errorBox.hidden = false;
    } finally {
      this.#cartBusy = false;
      this.#save();
      if (this.isConnected) this.#cartUi();
    }
  }

  // ---------- result ----------

  /** Product currently chosen for a step. */
  #current(step) {
    const chosen = this.#state.picks[step.step];
    if (chosen) {
      const product = [step.pick, ...(step.alternatives || [])].find((item) => item?.handle === chosen);
      if (product) return product;
    }
    return step.pick;
  }

  /** Times of day of a step — the chosen product decides (e.g. a retinol serum only in the evening). */
  #when(step) {
    const product = this.#current(step);
    return Array.isArray(product?.when) && product.when.length ? product.when : step.when || [];
  }

  /** Unique products of the routine (morning and evening share most of them). */
  #products() {
    const seen = new Map();
    for (const step of this.#state.routine?.steps || []) {
      const product = this.#current(step);
      if (product && !seen.has(product.handle)) seen.set(product.handle, product);
    }
    return [...seen.values()];
  }

  #renderResult() {
    const { routine, profile, time } = this.#state;
    if (!routine) return;
    const steps = routine.steps;
    const count = steps.length;
    this.querySelector('[data-shortfall]')?.toggleAttribute('hidden', count >= Number(profile.steps));
    this.querySelector('[data-result-title]').innerHTML = this.#t('result_title', { count });

    const tags = [];
    if (profile.hauttyp) tags.push(this.#opt(profile.hauttyp));
    if (profile.empfindlich) tags.push(this.#t('profile_sensitive'));
    for (const need of profile.bedarf || []) tags.push(this.#opt(need));
    for (const avoid of profile.meiden || []) tags.push(`${this.#t('label_meiden')}: ${this.#opt(avoid)}`);
    if (profile.budget && profile.budget !== 'egal') tags.push(this.#t(`profile_budget_${keyOf(profile.budget)}`));
    this.querySelector('[data-result-tags]').innerHTML = tags.map((tag) => `<li>${tag}</li>`).join('');

    for (const button of this.querySelectorAll('[data-time]')) {
      const key = /** @type {HTMLElement} */ (button).dataset.time;
      button.setAttribute('aria-pressed', String(key === time));
      const counter = this.querySelector(`[data-count="${key}"]`);
      if (counter) counter.textContent = String(steps.filter((step) => this.#when(step).includes(key)).length);
    }

    const visible = steps.filter((step) => this.#when(step).includes(time));
    const list = this.querySelector('[data-steps]');
    list.innerHTML = visible.map((step, index) => this.#stepHtml(step, index + 1)).join('');
    this.#renderSummary();
  }

  #stepHtml(step, number) {
    const product = this.#current(step);
    const label = step.label || this.#i18n[`step_${keyOf(step.step)}`] || step.step;
    const others = [step.pick, ...(step.alternatives || [])].filter((item) => item && item.handle !== product?.handle);
    const open = this.#openAlternatives.has(step.step);
    const badges = [];
    if (step.frequency) badges.push(`<span class="kunnie-rb__badge kunnie-rb__badge--rose">${escapeHtml(step.frequency)}</span>`);
    const when = this.#when(step);
    if (when.length === 1) badges.push(`<span class="kunnie-rb__badge">${this.#t(when[0] === 'morgens' ? 'only_morning' : 'only_evening')}</span>`);
    const panelId = `KunnieRbAlt-${escapeHtml(step.step)}`;

    return `
      <li class="kunnie-rb__step" style="--i: ${number - 1}">
        <span class="kunnie-rb__step-num" aria-hidden="true">${number}</span>
        <div class="kunnie-rb__step-body">
          <div class="kunnie-rb__step-head">
            <h3 class="kunnie-rb__step-title"><span class="visually-hidden">${this.#t('step_n', { n: number })}: </span>${escapeHtml(label)}</h3>
            ${badges.join('')}
          </div>
          ${
            product
              ? `${product.reason ? `<p class="kunnie-rb__reason"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l2.2 5.3L19.5 10.5l-5.3 2.2L12 18l-2.2-5.3L4.5 10.5l5.3-2.2z" fill="currentColor"/></svg>${escapeHtml(product.reason)}</p>` : ''}
                 <div class="kunnie-rb__card" data-card="${escapeHtml(product.handle)}">${this.#cardFallback(product)}</div>
                 `
              : `<p class="kunnie-rb__muted">${this.#t('step_empty')}</p>`
          }
          ${
            others.length
              ? `<button type="button" class="kunnie-rb__alt-toggle" data-alt-toggle="${escapeHtml(step.step)}" aria-expanded="${open}" aria-controls="${panelId}">
                   ${open ? this.#t('alt_close') : this.#t('alt_open', { count: others.length })}
                   <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                 </button>
                 <div class="kunnie-rb__alts" id="${panelId}" ${open ? '' : 'hidden'}>
                   <ul class="kunnie-rb__alt-list list-unstyled">
                     ${others
                       .map(
                         (item) => `
                       <li class="kunnie-rb__alt">
                         <div class="kunnie-rb__card kunnie-rb__card--alt" ${open ? `data-card="${escapeHtml(item.handle)}"` : ''}>${this.#cardFallback(item)}</div>
                         ${item.reason ? `<p class="kunnie-rb__alt-reason">${escapeHtml(item.reason)}</p>` : ''}
                         <button type="button" class="button button-secondary kunnie-rb__alt-pick" data-pick-step="${escapeHtml(step.step)}" data-pick-handle="${escapeHtml(item.handle)}">${this.#t('alt_pick')}</button>
                       </li>`
                       )
                       .join('')}
                   </ul>
                 </div>`
              : ''
          }
        </div>
      </li>`;
  }

  #renderSummary() {
    const products = this.#products();
    const total = products.reduce((sum, product) => sum + (Number(product.price) || 0), 0);
    this.querySelector('[data-thumbs]').innerHTML = products
      .map(
        (product) =>
          `<li title="${escapeHtml(product.title)}">${product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy" width="56" height="56">` : ''}</li>`
      )
      .join('');
    this.querySelector('[data-products-count]').textContent = this.#i18n.products_count?.replace('%count%', String(products.length)) ?? '';
    this.querySelector('[data-total]').textContent = this.#money(total);

    const limits = { 'bis-50': 50, 'bis-100': 100 };
    const limit = limits[this.#state.profile.budget];
    const note = this.querySelector('[data-budget-note]');
    if (note) {
      note.hidden = !(limit && (this.#state.routine?.budgetExceeded || total > limit));
      if (limit) note.textContent = this.#i18n.budget_over?.replace('%budget%', this.#money(limit)) ?? '';
    }

    const name = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-save-name]'));
    if (name && !name.value) name.value = this.#defaultName();
    if (this.#state.savedId) this.#renderSavedStatus();
    else if (this.querySelector('[data-save-status]')) this.querySelector('[data-save-status]').innerHTML = '';
    const saveButton = this.querySelector('[data-save-button]');
    if (saveButton) saveButton.disabled = Boolean(this.#state.savedId) || !products.length;
    this.#cartUi();
  }

  #defaultName() {
    const date = new Intl.DateTimeFormat(this.#locale, { day: 'numeric', month: 'short' }).format(new Date());
    return `${this.#i18n.save_default_name || 'Routine'} · ${date}`;
  }

  /** @param {string} step @param {string} handle */
  #pickAlternative(step, handle) {
    const routineStep = this.#state.routine?.steps.find((item) => item.step === step);
    if (!routineStep || ![routineStep.pick, ...(routineStep.alternatives || [])].some(p => p.handle === handle)) return;
    if (this.#state.cartState === 'added') { this.#state.cartState = 'ready'; this.#state.cartId = ''; }
    this.#state.picks[step] = handle;
    this.#state.savedId = null;
    this.#openAlternatives.delete(step);
    this.#save();
    this.#renderResult();
    const product = this.#current(routineStep);
    this.#announce(this.#i18n.alt_picked?.replace('%title%', product?.title || '').replace('%step%', routineStep.label || step) || '');
    /** @type {HTMLElement | null} */ (this.querySelector(`[data-alt-toggle="${CSS.escape(step)}"]`))?.focus();
    const status = this.querySelector('[data-save-status]');
    if (status) status.innerHTML = '';
  }

  /** @param {string} step */
  #toggleAlternatives(step) {
    if (this.#openAlternatives.has(step)) this.#openAlternatives.delete(step);
    else this.#openAlternatives.add(step);
    const button = this.querySelector(`[data-alt-toggle="${CSS.escape(step)}"]`);
    const panel = button && this.querySelector(`#${button.getAttribute('aria-controls')}`);
    if (!button || !panel) return;
    const open = this.#openAlternatives.has(step);
    const routineStep = this.#state.routine?.steps.find((item) => item.step === step);
    const others = routineStep ? [routineStep.pick, ...(routineStep.alternatives || [])].filter((item) => item && item.handle !== this.#current(routineStep)?.handle) : [];
    button.setAttribute('aria-expanded', String(open));
    button.innerHTML = `${open ? this.#t('alt_close') : this.#t('alt_open', { count: others.length })}<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    panel.toggleAttribute('hidden', !open);
    if (open) {
      panel.querySelectorAll('.kunnie-rb__card--alt').forEach((slot, index) => {
        if (others[index]) /** @type {HTMLElement} */ (slot).dataset.card = others[index].handle;
      });
    }
  }

  #onSave = async (event) => {
    event.preventDefault();
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-save-button]'));
    const status = this.querySelector('[data-save-status]');
    if (!button || !status || this.#state.savedId || button.getAttribute('aria-busy') === 'true') return;
    const name = /** @type {HTMLInputElement} */ (this.querySelector('[data-save-name]')).value.trim() || this.#defaultName();
    const items = (this.#state.routine?.steps || [])
      .map((step) => ({ step: step.step, product: this.#current(step) }))
      .filter(({ product }) => product)
      .map(({ step, product }) => ({ step, handle: product.handle, variantId: product.variantId }));
    button.setAttribute('aria-busy', 'true');
    status.className = 'kunnie-rb__save-status';
    try {
      const result = await this.#api({ name, items });
      if (result?.loggedIn === false) {
        status.innerHTML = `${this.#t('save_login')} <a href="${escapeHtml(this.dataset.loginUrl)}">${this.#t('login')}</a>`;
        return;
      }
      if (!result?.ok) throw new Error(result?.error || 'save failed');
      this.#state.savedId = result.id || 'saved';
      this.#save();
      this.#renderSavedStatus();
    } catch (error) {
      status.classList.add('is-error');
      const limit = error instanceof Error && /limit|max/i.test(error.message);
      status.textContent = limit ? this.#i18n.save_limit || '' : this.#i18n.save_error || '';
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  #renderSavedStatus() {
    const status = this.querySelector('[data-save-status]');
    if (!status) return;
    status.className = 'kunnie-rb__save-status is-success';
    status.innerHTML = `${this.#t('save_saved')} <a href="${escapeHtml(this.dataset.accountUrl)}">${this.#t('save_link')}</a>`;
  }

  // ---------- events ----------

  #restart() {
    this.#run++;
    clearTimeout(this.#advanceTimer);
    session.clear();
    this.#state = freshState();
    this.#busy = false;
    this.#openAlternatives.clear();
    const status = this.querySelector('[data-save-status]');
    if (status) status.innerHTML = '';
    const name = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-save-name]'));
    if (name) name.value = '';
    this.#go('entry');
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (this.#cartBusy) return;
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('[data-start]')) return this.#start();

    const option = target.closest('[data-field]');
    if (option) return this.#choose(/** @type {HTMLElement} */ (option));

    if (target.closest('[data-next]')) return this.#next();
    if (target.closest('[data-back]')) return this.#back();
    if (target.closest('[data-restart]')) return this.#restart();
    if (target.closest('[data-build]')) return this.#build();
    if (target.closest('[data-edit]')) {
      this.#state.question = 0;
      this.#go('cards');
      return this.#announceQuestion();
    }

    const time = target.closest('[data-time]');
    if (time) {
      this.#state.time = /** @type {HTMLElement} */ (time).dataset.time || 'morgens';
      this.#save();
      return this.#renderResult();
    }

    const toggle = target.closest('[data-alt-toggle]');
    if (toggle) return this.#toggleAlternatives(/** @type {HTMLElement} */ (toggle).dataset.altToggle || '');

    const pick = target.closest('[data-pick-step]');
    if (pick) {
      const { pickStep, pickHandle } = /** @type {HTMLElement} */ (pick).dataset;
      return this.#pickAlternative(pickStep || '', pickHandle || '');
    }

    const cart = target.closest('[data-cart-add]');
    if (cart) return this.#addAllToCart(/** @type {HTMLElement} */ (cart));


  };
}

if (!customElements.get('kunnie-routine-builder')) customElements.define('kunnie-routine-builder', KunnieRoutineBuilder);
